<template>
    <b-card
        header="Data Collection Overview"
        v-if="datasets?.length">
        <div class="row">
            <div class="col">
                <form class="d-flex mb-3">
                    <label
                        for="dataset"
                        class="align-self-center me-3"
                        ><h3 class="mb-0">Data Collection</h3>
                    </label>
                    <div>
                        <select
                            class="form-select"
                            v-model="selectedDataset"
                            id="dataset">
                            <option
                                v-for="dataset in datasets"
                                :value="dataset.value">
                                {{ dataset.name }}
                            </option>
                        </select>
                    </div>
                </form>
            </div>
        </div>

        <div class="block">
            <div class="row mb-2">
                <div
                    class="col"
                    v-if="vendor">
                    <span class="h5">Start Date: </span>
                    {{ vendor.agency_start_date }}
                </div>
                <div
                    class="col"
                    v-if="vendor">
                    <span class="h5">Vendor: </span>
                    {{ vendor.vendor_name }}
                </div>
                <div class="col"></div>
            </div>
            <div
                class="row"
                v-if="store?.datasetSummary">
                <div class="col">
                    <span class="h5">Outstanding Errors: </span>
                    <span class="text-danger fw-bold">{{ store.datasetSummary.error_count }} error{{ store.datasetSummary.error_count !== 1 ? 's' : '' }}</span>
                </div>
                <div class="col">
                    <span class="h5">Outstanding Warnings: </span>
                    <span class="text-warning fw-bold">{{ store.datasetSummary.warning_count }} warning{{ store.datasetSummary.warning_count !== 1 ? 's' : '' }}</span>
                </div>
                <div class="col text-end">
                    <a :href="`ErrorsAndWarnings.aspx?source=${selectedDataset}`"
                        >View Outstanding Errors and Warnings</a
                    >
                </div>
            </div>
        </div>

        <h3 class="mt-4">Last Submission</h3>
        <div
            class="block"
            v-if="store?.datasetLastSubmissions">
            <b-table
                :items="store.datasetLastSubmissions"
                :columns="lastSubmissionColumns"
                :caption="`Last ${title} Submission`"
                :aria-label="`Last ${title} Submission`"
                :value="lastSubmissionTableOptions">
                <template v-slot:cell.error_rate="{item, column}"> {{ formatPercent(item.error_rate) }} </template>
            </b-table>
        </div>

        <div class="row mb-3">
            <div class="col-auto d-flex mb-0">
                <label for="submissionOverviewPeriod" class="align-self-center mb-0">
                    <h3 class="mb-0">Submission Overview for</h3>
                </label>
            </div>
            <div class="col-auto">
                <select
                    id="submissionOverviewPeriod"
                    v-model="selectedPeriod"
                    class="form-select">
                    <option
                        v-for="item in periods"
                        :value="item.value">
                        {{ item.name }}
                    </option>
                </select>
            </div>

            <div class="col text-end">
                <a :href="`dataupload.aspx?source=${selectedDataset}`">View Upload History</a>
            </div>
        </div>
        <div
            class="block"
            v-if="store?.datasetStatisticsSummary">
            <b-table
                :items="store.datasetStatisticsSummary"
                :columns="statisticsColumns"
                :caption="`${title} Incidents Uploaded`"
                :aria-label="`${title} Incidents Uploaded`"
                :value="statisticsTableOptions">
                <template v-slot:cell.error_rate="{item, column}"> {{ formatPercent(item.error_rate) }} </template>
            </b-table>
        </div>
    </b-card>
</template>
<script setup lang="ts">
import {computed, onBeforeMount, onMounted, reactive, ref, watch} from "vue";
import {BCard} from "./../Misc";
import {UploadType} from "@/types";
import {useContactsStore} from "@/stores/contactsStore";
import {useCommunityPolicingStore} from "@/stores/communityPolicingStore";
import {useNibrsStore} from "@/stores/nibrsStore";
import {useSummaryStore} from "@/stores/summaryStore";
import {useUofStore} from "@/stores/uofStore";
import {usePoliceEmployeeStore} from "@/stores/policeEmployeeStore";
import {BTableColumn, BTableOptions} from "../BTable/BTable.types";
import BTable from "../BTable/BTable.vue";
import {useOriStore} from "@/stores/oriStore";
import hasPermission from "@/composable/permissionHelper";
import {formatPercent} from "@/utils";
const props = defineProps({
    ori: {
        type: String,
        default: null,
    },
});

const selectedDataset = ref();
const selectedPeriod = ref(0);
const datasets = ref<any[]>();
const oriStore = useOriStore();

function filterDatasets() {
    var result: any[] = [];
    if (oriStore.ori != null) {
        oriStore.ori.data_collection_status.forEach((element) => {            

            if (element.agency_enabled) {
                result = result.concat(datasetsDefinition.filter((x) => x.code == element.name));
            }
        });
    }

    datasets.value = result.filter((x) => hasPermission(x.permission));
}

const title = computed(() => {
    return selectedDatasetDefinition.value!.name;
});

const selectedDatasetDefinition = computed(() =>
    datasetsDefinition.findLast((it) => it.value == selectedDataset.value)
);

const vendor = computed(() => {
    if (!oriStore.ori || !selectedDatasetDefinition.value) {
        return null;
    }
    return oriStore.ori.data_collection_status.findLast((x) => x.label == selectedDatasetDefinition.value!.name);
});
const statisticsTableOptions: BTableOptions = {
    itemsPerPage: 1,
    page: 1,
    sortBy: [],
    totalItemCount: 0,
    pagingMode: "noTotal",
};
const lastSubmissionTableOptions: BTableOptions = {
    itemsPerPage: 1,
    page: 1,
    sortBy: [],
    totalItemCount: 0,
    pagingMode: "noTotal",
};

const statisticsColumns = reactive<BTableColumn[]>([
    {
        key: "reports_processed",
        text: "Total Records",
        sortable: false,
        columnClass: ["align-top"],
        value: (item) => item.reports_processed,
    },
    {
        key: "reports_rejected",
        text: "Rejected",
        sortable: false,
        columnClass: ["align-top"],
        value: (item) => item.reports_rejected,
    },
    {
        key: "error_rate",
        text: "Error Rate",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.error_rate,
    },
]);

const lastSubmissionColumns = reactive<BTableColumn[]>([
    {
        key: "upload_date",
        text: "Date/Time",
        sortable: false,
        columnClass: ["align-top"],
        value: (item) => item.upload_date,
    },
    {
        key: "file_name",
        text: "Filename",
        sortable: false,
        columnClass: ["align-top"],
        value: (item) => item.file_name,
    },
    {
        key: "uploaded_by",
        text: "Uploaded By",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.uploaded_by,
    },
    {
        key: "total_records",
        text: "Total Records",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.total_records,
    },
    {
        key: "rejected",
        text: "Rejected",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.rejected,
    },
    {
        key: "error_rate",
        text: "Error Rate",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        value: (item) => item.error_rate,
    },
]);

const store = computed(() => {
    var uploadType: UploadType = selectedDataset.value;

    switch (uploadType) {
        case UploadType.contacts:
            return useContactsStore();

        case UploadType.communityPolicing:
            return useCommunityPolicingStore();

        case UploadType.nibrs:
            return useNibrsStore();

        case UploadType.summary:
            return useSummaryStore();

        case UploadType.useOfForce:
            return useUofStore();

        case UploadType.policeEmployee:
            return usePoliceEmployeeStore();
    }
});

watch(props, async () => {
    if (!props.ori) {
        return;
    }
    await oriStore.getOri(props.ori);

    filterDatasets();
    if (datasets.value && datasets.value.length) {
        selectedDataset.value = datasets.value[0].value;
    }
    if (store.value) {
        await store.value.loadErrorsSummary(props.ori);
        await store.value.loadStatisticsSummary(selectedPeriod.value, props.ori);
        await store.value.loadLastSubmission(props.ori);
    }
});
watch(selectedDataset, async () => {
    if (!props.ori) {
        return;
    }
    if (store.value) {
        await store.value.loadErrorsSummary(props.ori);
        await store.value.loadStatisticsSummary(selectedPeriod.value, props.ori);
        await store.value.loadLastSubmission(props.ori);
    }
});
watch(selectedPeriod, async () => {
    if (!props.ori) {
        return;
    }
    if (store.value) {
        await store.value.loadStatisticsSummary(selectedPeriod.value, props.ori);
        await store.value.loadLastSubmission(props.ori);
    }
});

const periods = [
    {name: "Today", value: 0},
    {name: "This Week", value: 1},
    {name: "This Month", value: 2},
    {name: "This Year", value: 3},
];

const datasetsDefinition = [
    {name: "NIBRS", value: 0, permission: "dataupload", code: "NIBRS"},
    {name: "Use of Force", value: 2, permission: "useOfForceDataUpload", code: "UOF"},
    {name: "Community Policing", value: 6, permission: "CommunityPolicingDataUpload", code: "VACP"},
    {name: "Contacts", value: 5, permission: "ContactDataUpload", code: "CONTACTS"},
];
</script>
